import 'typeahead.js';

import $ from 'jquery';
import Places from 'places.js';

import {
    ARROW_DOWN_KEYCODE,
    ENTER_KEYCODE,
    TAB_KEYCODE,
    TYPEAHEAD_DEBOUNCE_MODE,
    TYPEAHEAD_DEBOUNCE_TIME,
} from './constants.es6';
import Ga from './ga';
import MAEvent from './ma-event';
import { bindForm } from './tagging';
import { addURLParams, createFunctionWithTimeout } from './utils';

require('select2')();
require('select2/dist/js/i18n/fr.js');

export function clearHiddenFields($form) {
    const fields = [
        'item_city_id',
        'item_city_name',
        'address_lng',
        'address_lat',
        'item_zip',
        'item_address',
    ];

    fields.forEach(e => {
        $form.find(`:input[name=${e}]`).remove();
    });
}

/**
 * Search a place for pre-filling estima form page
 * @private
 */
function _defaultAddressChangedHandler(field, place, callback) {
    let zip = null;

    let cityName = null;

    const $field = $(field);

    const $form = $(field.form);

    clearHiddenFields($form);

    $form.prepend($('<input>', { name: 'item_address', type: 'hidden', value: place.name }));
    $form.prepend($('<input>', { name: 'address_lat', type: 'hidden', value: place.latlng.lat }));
    $form.prepend($('<input>', { name: 'address_lng', type: 'hidden', value: place.latlng.lng }));

    if (place.postcode) {
        zip = place.postcode;
        $form.prepend($('<input>', { name: 'item_zip', type: 'hidden', value: zip }));
    } else if ($('input[name="item_zip"]')) {
        $('input[name="item_zip"]').remove();
    }

    if (place.city) {
        cityName = place.city;
    }

    // search city id using geo API and submit the form
    $.get(
        MA.Config.GEOAPI_URL,
        {
            q: `${zip} ${cityName}`,
            types: 'cities,subcities,arrmuns',
            limit: MA.Config.GEOAPI_MAX_RESULTS,
        },
        data => {
            if (data.response.places && data.response.places.length > 0) {
                $form.prepend(
                    $('<input>', {
                        name: 'item_city_name',
                        type: 'hidden',
                        value: data.response.places[0].name_short,
                    }),
                );
            }
            if (data.response.places && data.response.places.length > 0) {
                $form.prepend(
                    $('<input>', {
                        name: 'item_city_id',
                        type: 'hidden',
                        value: data.response.places[0].id,
                    }),
                );
            }
            Ga.send({
                hitType: 'event',
                eventCategory: $field.data('ua-event-category'),
                eventAction: $field.data('ua-event-action'),
                eventLabel: $field.data('ua-event-label'),
                hitCallback: createFunctionWithTimeout(() => {
                    if (typeof callback === 'function') {
                        callback();
                    }
                }),
            });
        },
    );
}

/**
 * Handling address search box behavior for estima page (and home page call action)
 *
 * @param {nodeList} searchBoxes nodeList of search box elements
 * @param {boolean} noAutoSubmitForm a boolean to know if the form should not be automatically sent
 * @param placeChangedHandler A callback that will be called when selection change
 */
export function addressSearchBoxesHandler(searchBoxes, noAutoSubmitForm, placeChangedHandler) {
    searchBoxes.forEach(function addSearchBoxHandler(box) {
        const field = box;

        const autocomplete = Places({
            appId: MA.Config.ALGOLIA_APP_ID,
            apiKey: MA.Config.ALGOLIA_SEARCH_API_KEY,
            container: field,
            countries: ['FR'],
            type: 'address',
        });

        autocomplete.on('change', e => {
            const place = e.suggestion;
            field.textContent = place.value;

            if (typeof placeChangedHandler === 'function') {
                placeChangedHandler(field, place);
            } else {
                _defaultAddressChangedHandler(field, place, () => {
                    if (!noAutoSubmitForm) {
                        $(field.form).submit();
                    }
                });
            }
        });

        // prevent form submission when using return to select a place
        $(this).keypress(e => {
            if (e.keyCode === 13) {
                e.preventDefault();
            }
        });
    });
}

/**
 *
 * @param responseXhr
 * @returns {Array}
 * @private
 */
function _typeaheadPlacesFilterMatches(responseXhr) {
    return responseXhr.response.places.map(place => ({
        ...place,
        tags: place._tags,
        title: place.highlighted_title,
    }));
}

/**
 *
 * @param tags
 * @returns {string}
 * @private
 */
function _tagsToLabel(tags) {
    const labels = {
        regions: 'Région',
        subregions: 'Dépt.',
        streets: 'Rue',
        addresses: 'Adr.',
        arrmuns: 'Arr.',
        boroughs: 'Qrt.',
        neighborhoods: 'Qrt.',
        subcities: 'Ville',
        cities: 'Ville',
    };

    let label = 'tag';
    if (!tags) {
        return label;
    }
    $.each(tags, (i, tag) => {
        label = labels[tag];
        return label === undefined;
    });
    return label;
}

/**
 *
 * @param placeTypes
 * @returns {Array}
 */
export function typeaheadPlaceFieldDatasets(placeTypes) {
    const places = placeTypes.split(',');
    const typeaheadDatasets = [];

    const context = {
        name: 'geosearch',
        datums: null,
        url_api: MA.Config.GEOAPI_URL,
    };

    // We want to extract `adresses` and `streets` types to make separate xhr call
    // because GeoAPI does not handles search with these both types
    // Order is important
    let showAddresses = false;
    let showStreets = false;
    if (placeTypes.indexOf('addresses') !== -1) {
        showAddresses = true;
        places.splice(places.indexOf('addresses'), 1);
    }
    if (placeTypes.indexOf('streets') !== -1) {
        showStreets = true;
        places.splice(places.indexOf('streets'), 1);
    }

    // Rebuild types in wanted order
    const types = [];
    if (showAddresses) {
        types.push('addresses');
    }
    if (places.length > 0) {
        types.push(places.join(','));
    }
    if (showStreets) {
        types.push('streets');
    }

    types.forEach(tags => {
        const engine = new Bloodhound({
            datumTokenizer(d) {
                return Bloodhound.tokenizers.whitespace(d.title);
            },
            queryTokenizer: Bloodhound.tokenizers.whitespace,
            remote: {
                filter: _typeaheadPlacesFilterMatches,
                rateLimitBy: TYPEAHEAD_DEBOUNCE_MODE,
                rateLimitWait: TYPEAHEAD_DEBOUNCE_TIME,
                timeout: 30000,
                url: `${context.url_api}?q=%QUERY&types=${tags}`,
                wildcard: '%QUERY',
            },
        });
        engine.initialize();
        typeaheadDatasets.push({
            source: engine.ttAdapter(),
            displayKey: 'title',
            templates: {
                suggestion(datum) {
                    let title = datum._title ? datum._title : datum.title;
                    const label = _tagsToLabel(datum.tags);
                    if (title !== '' && label !== '') {
                        title += `<small class="tt-suggestion__sublabel">${label}</small>`;
                    }
                    return `<span>${title}</span>`;
                },
            },
        });
    });
    return typeaheadDatasets;
}

/**
 * Remove additional fields of a typeahead places search
 * @param {jQuery} $input Input field
 * @param {jQuery} $form Search form to submit
 * @private
 */
function _resetSearchForm($input, $form) {
    const fields = $input.data('add-fields');

    if (fields) {
        fields.split(/,\s?/).forEach(field => {
            const [key] = field.split(/:\s?/);
            const $field = $form.find(`[name=${key}]`);
            if ($field.length > 0) {
                $field.remove();
            }
        });
    }
}

/**
 * Submit a geo search form depending on a selected place
 * @param {jQuery} $input Input field
 * @param {jQuery} $form Search form to submit
 * @param {object} place Geo place object
 * @private
 */
function _submitPlaceSearchForm($input, $form, place) {
    const fields = $input.data('add-fields');

    if (fields) {
        fields.split(/,\s?/).forEach(field => {
            const [name, value] = field.split(/:\s?/);
            const $field = $form.find(`[name=${name}]`);
            if ($field.length > 0) {
                $field.val(place[value]);
            } else {
                $input.after($('<input>', { name, type: 'hidden', value: place[value] }));
            }
        });
    }

    if ($input.data('ua-event-action')) {
        $form.data('ua-event-action', `${$input.data('ua-event-action')}:${place.geotype_label}`);
    }

    if ($input.data('auto-submit')) {
        $form.submit();
    }
}

/**
 * @param {jQuery|string} selector A jQuery object or a jQuery selector string
 */
export function initializePlacesTypeahead(selector) {
    let target;
    if (typeof selector === 'string') {
        target = $(selector);
    } else if (typeof selector === 'object') {
        // jQuery object
        target = selector;
    } else {
        target = $('[data-places-typeahead]');
    }

    // Realtor search
    target.each(function initializePlaceTypeahead() {
        const $input = $(this);
        $input.typeahead(
            {
                hint: true,
                highlight: true,
                minLength: 1,
            },
            typeaheadPlaceFieldDatasets($input.data('place-types')),
        );

        // set hidden input on typeahead selection
        $input.on('typeahead:selected', (event, place) => {
            $input.data('typeahead-selected', true);
            _submitPlaceSearchForm($input, $(event.target.form), place);

            $input.trigger({
                type: MAEvent.placeSelected,
                place,
                time: new Date(),
            });

            if ($input.data('ua-event-action')) {
                Ga.send({
                    hitType: 'event',
                    eventCategory: $input.data('ua-event-category'),
                    eventAction: $input.data('ua-event-action'),
                    eventLabel: $input.data('ua-event-label'),
                });
            }
        });

        // on exit field and if user did not select an option, empty field
        $input.on('blur', event => {
            if ($input.typeahead('val') === '') {
                $input.data('typeahead-selected', false);
                _resetSearchForm($input, $(event.target.form));
                $input.trigger({
                    type: MAEvent.placeSelected,
                    place: null,
                    time: new Date(),
                });
            }
        });
    });
}

/**
 * Search box to search places with tags
 * @param {String} elementsSelector jQuery selector
 * @param {Object} overriddenOptions Option to pass to select2
 */
export function initializeMultiplePlaceSearch(elementsSelector, overriddenOptions) {
    const $elements = $(!elementsSelector ? '[data-places-search]' : elementsSelector);

    if ($elements.length === 0) {
        return;
    }

    const defaultOptions = {
        width: 'container',
        dropdownParent: $('[data-select2-placeholder]'),
        minimumInputLength: 1,
        delay: 100,
        templateResult(place) {
            return place._title ? $(`<span>${place._title}</span>`) : undefined;
        },
        templateSelection(place) {
            return place.text ? place.text : undefined;
        },
        placeTypeIdsInputs: false,
    };

    const options = $.extend({}, defaultOptions, overriddenOptions);

    $elements.each(function initializePlaceSearch() {
        const $this = $(this);

        const $form = $(this.form);

        const isIE10 = !!navigator.userAgent.match(/MSIE 10/i);

        const isIE11 = !!navigator.userAgent.match(/Trident.*rv:11\./);

        const placeholder = isIE11 || isIE10 ? '' : $this.data('placeholder');

        if (!options.placeholder) {
            options.placeholder = placeholder;
        }

        // we remove placeholder in IE 10+ because data-placeholder in select2 = bug
        if (isIE11 || isIE10) {
            $this.removeAttr('data-placeholder');
        }

        if (!options.ajax) {
            options.ajax = {
                url: MA.Config.GEOAPI_URL,
                dataType: 'json',
                data(params) {
                    return {
                        q: params.term,
                        types: $this.data().placeTypes,
                        limit: MA.Config.GEOAPI_MAX_RESULTS,
                    };
                },
                processResults(data) {
                    const places = [];
                    $.each(data.response.places, function processResult() {
                        const title = this.title.replace(/\s+\([0-9]+\)/, ''); // Remove postal code
                        places.push({
                            id: `${this.id}|${title}`,
                            _id: this.id,
                            text: title,
                            _title: this._title,
                            _tags: this._tags,
                        });
                    });
                    return {
                        results: places,
                    };
                },
                cache: true,
            };
        }

        $this.select2(options);

        // add hidden input associated with select element
        $this.on('select2:select', e => {
            if (options.placeTypeIdsInputs === false) {
                let inputName = 'place_ids';
                if ($this.attr('data-to-old-stack') !== undefined) {
                    inputName = 'place_ids[]';
                }
                $form.append(
                    $('<input>', {
                        name: inputName,
                        type: 'hidden',
                        value: e.params.data._id,
                    }),
                );
            } else {
                Object.keys(MA.Config.PLACE_TYPE_PLURALS || {}).forEach(placeTypePluralKey => {
                    if (
                        e.params.data._tags.includes(
                            MA.Config.PLACE_TYPE_PLURALS[placeTypePluralKey],
                        )
                    ) {
                        $form.append(
                            $('<input>', {
                                name: `${placeTypePluralKey}_ids`,
                                type: 'hidden',
                                value: e.params.data._id,
                            }),
                        );
                    }
                });
            }
        });

        // remove hidden input associated with select element
        $this.on('select2:unselect', e => {
            $form.find(`input[value=${e.params.data.id.split('|')[0]}]`).remove();
        });

        if ($this.data('ua-event-category')) {
            $form.on('submit', () => {
                Ga.send({
                    hitType: 'event',
                    eventCategory: $this.data('ua-event-category'),
                    eventAction: $this.data('ua-event-action'),
                    eventLabel: $this.data('ua-event-label'),
                });
            });
        }
    });
}

/**
 *
 * @param responseXhr
 * @returns {Array}
 * @private
 */
function _typeaheadRealtorsFilterMatches(responseXhr) {
    const datums = [];

    $.each(responseXhr, (_, realtor) => {
        datums.push(realtor);
    });
    return datums;
}

/**
 *
 * @returns {Array}
 * @private
 */
function _typeaheadRealtorFieldDatasets($input) {
    const typeaheadDatasets = [];

    const engine = new Bloodhound({
        datumTokenizer(d) {
            return Bloodhound.tokenizers.whitespace(d.title);
        },

        queryTokenizer: Bloodhound.tokenizers.whitespace,
        remote: {
            ajax: {
                beforeSend(_, settings) {
                    // must use Object.assign here because settings object must be patched
                    return Object.assign(settings, {
                        type: 'GET',
                        hasContent: true,
                        url: addURLParams(settings.url, {
                            name: $input.val(),
                        }),
                    });
                },
            },
            filter: _typeaheadRealtorsFilterMatches,
            rateLimitBy: TYPEAHEAD_DEBOUNCE_MODE,
            rateLimitWait: TYPEAHEAD_DEBOUNCE_TIME,
            timeout: 30000,
            cache: false,
            url: $input.data('url'),
        },
    });
    engine.initialize();
    typeaheadDatasets.push({
        source: engine.ttAdapter(),
        displayKey: 'name',
        templates: {
            suggestion(datum) {
                const name = datum._name ? datum._name : datum.name;
                return `<span>${name}</span>`;
            },
        },
    });
    return typeaheadDatasets;
}

/**
 *
 */
export function initializeRealtorsTypeahead() {
    $(document).ready(() => {
        // Realtor search
        $('[data-realtors-search-name]').each(function initializeRealtorTypeahead() {
            const $input = $(this);
            $input.typeahead(
                {
                    hint: true,
                    highlight: true,
                    minLength: 1,
                    autoselect: false,
                },
                _typeaheadRealtorFieldDatasets($input),
            );

            // set hidden input on typeahead selection
            $input.on('typeahead:selected', (event, realtor) => {
                $input.trigger({
                    type: MAEvent.realtorSelected,
                    realtor,
                    time: new Date(),
                });
                // _submitRealtorSearchForm($input, $(event.target.form), realtor);
                $input.data('typeahead-selected', true);
                if ($input.data('ua-event-action')) {
                    Ga.send({
                        hitType: 'event',
                        eventCategory: $input.data('ua-event-category'),
                        eventAction: $input.data('ua-event-action'),
                        eventLabel: $input.data('ua-event-label'),
                    });
                }
            });
        });
    });
}

/**
 * Add keyboard behavior to a desired search field
 *
 * @param {jQuery} $searchField jQuery element of the search field
 * @param {Object} [parameters] Optional parameters to customize behavior
 * @param {Function} [parameters.resetHandler] Handle reset of search field state
 */
export function handleSearchField($searchField, { resetHandler = () => {} } = {}) {
    return function searchFieldHandler(event) {
        resetHandler($searchField);

        const arrowKeydown = $.Event('keydown', {
            keyCode: ARROW_DOWN_KEYCODE,
            which: ARROW_DOWN_KEYCODE,
        });
        const tabKeydown = $.Event('keydown', { keyCode: TAB_KEYCODE, which: TAB_KEYCODE });

        $searchField.off('keydown', handleSearchField($searchField, { resetHandler }));

        const $cursor = $('.tt-suggestion.tt-cursor');
        if (event.which === ENTER_KEYCODE) {
            if ($cursor.length > 0) {
                $cursor.click();
            } else {
                $(this).trigger(arrowKeydown);
                $(this).trigger(tabKeydown);
            }
        } else if (event.which === TAB_KEYCODE) {
            if ($cursor.length > 0) {
                $cursor.click();
            } else {
                $(this).trigger(arrowKeydown);
                $('.tt-suggestions')
                    .children()
                    .first()
                    .click();
            }
        }
    };
}

/**
 * Add behavior to Estima place search form
 */
export function initializeEstimaPlaceSearch(gaProperties) {
    // TODO refacto this using geoAPI RBV-70
    addressSearchBoxesHandler(document.querySelectorAll('[data-estima-place-search]'));

    const $estimaPlaceForm = $('[data-estima-place-form]');

    bindForm($estimaPlaceForm, gaProperties);

    $estimaPlaceForm.one('keyup', '[data-estima-place-search]', () =>
        $estimaPlaceForm.trigger(MAEvent.formStarted),
    );
}
